// ============================================================================
// PostingModal V3 — Com máscara monetária brasileira (pt-BR)
// Separador de milhar: ponto (.) | Decimal: vírgula (,)
// O valor é armazenado como dígitos brutos (ex: "150000" = R$ 1.500,00)
// ============================================================================

import React, { useState, useEffect, useMemo } from "react";
import { DEFAULT_CATEGORIES } from "../domain/categories.js";
import {
  TEMPLATE_MAP,
  ALL_TEMPLATE_IDS,
  ACCOUNT_OPTIONS,
} from "../domain/templates.js";
import {
  TaxonomyCombobox,
  normalizeTaxonomyValue,
} from "./TaxonomyCombobox.jsx";

/** Gera uma chave de grupo previsível a partir do rótulo digitado
 * (maiúscula, sem acento/espaço) — ex: "Cartão de Crédito" -> "CARTAO_DE_CREDITO". */
function slugifyGroupKey(label) {
  return normalizeTaxonomyValue(label)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const LABEL = "block text-[10px] font-bold text-gray-400 uppercase mb-1";
const INPUT =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-[#C5A059] focus:border-transparent outline-none transition";
const SELECT =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-800 focus:ring-2 focus:ring-[#C5A059] focus:border-transparent outline-none transition";
const SECTION = "bg-white border border-gray-200 rounded-lg p-4 space-y-3";
const SEC_TITLE =
  "text-[11px] font-bold text-[#20243B] uppercase tracking-wider flex items-center gap-2 pb-1.5 border-b border-gray-300";

const todayISO = () => new Date().toISOString().split("T")[0];
const currMonth = () => new Date().toISOString().slice(0, 7);

/** Converte string de dígitos brutos para número float (ex: "150000" → 1500.00) */
const rawDigitsToNumber = (raw) => {
  const digits = String(raw || "0").replace(/\D/g, "");
  return parseInt(digits || "0", 10) / 100;
};

/** Formata dígitos brutos para moeda brasileira (ex: "150000" → "1.500,00") */
const formatBRL = (raw) => {
  if (!raw && raw !== 0) return "";
  const num = rawDigitsToNumber(raw);
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Handler onChange para campos monetários: extrai apenas dígitos do que foi digitado */
const onCurrencyChange = (setter, field) => (e) => {
  const digits = e.target.value.replace(/\D/g, "");
  setter((prev) => ({ ...prev, [field]: digits }));
};

/** Cria um input de moeda com máscara brasileira */
const CurrencyInput = ({
  value,
  onChange,
  placeholder = "0,00",
  className = "",
}) => (
  <input
    className={INPUT + " text-right font-mono " + className}
    type="text"
    inputMode="numeric"
    placeholder={placeholder}
    value={formatBRL(value)}
    onChange={onChange}
  />
);

const buildTemplateOptions = () => {
  if (!TEMPLATE_MAP) return [];
  return ALL_TEMPLATE_IDS.filter(
    (id) => TEMPLATE_MAP[id]?.active !== false,
  ).map((id) => {
    const t = TEMPLATE_MAP[id];
    const s = t?.suds || {};
    return {
      value: id,
      label: `${id} — ${t.descIn || ""}`,
      group: t.group || "",
      sub: t?.sub || "",
      descIn: t?.descIn || "",
      descOut: t?.descOut || "",
      account: t?.account || "C6 PJ (Pix)",
      amount: t?.amount || t?.valorMin || 0,
      parcelado: t?.parcelado || false,
      recorrente: t?.recorrente || false,
      parcelaTotal: t?.parcelaTotal || null,
      suds: s,
    };
  });
};

const buildSudOptions = () => {
  const n1 = new Set(),
    n2 = new Set(),
    n3 = new Set();
  Object.values(TEMPLATE_MAP).forEach((t) => {
    if (t.suds?.nivel1) n1.add(t.suds.nivel1);
    if (t.suds?.nivel2) n2.add(t.suds.nivel2);
    if (t.suds?.nivel3) n3.add(t.suds.nivel3);
  });
  return {
    nivel1: [...n1].sort(),
    nivel2: [...n2].sort(),
    nivel3: [...n3].sort(),
  };
};

export function PostingModal({
  onClose,
  onSave,
  onSaveTemplate,
  categories,
  sud,
  naturezas,
  onAddSubcategory,
  onAddGroup,
  onAddSudValue,
  onAddNatureza,
  initialData,
  forceNew = false,
}) {
  const templateOptions = useMemo(buildTemplateOptions, []);
  // SUD vem do Firestore (SSOT) — buildSudOptions() do catálogo estático
  // fica só de fallback pra quando `sud` ainda não carregou.
  const staticSudFallback = useMemo(buildSudOptions, []);
  const sudOptions = {
    nivel1: sud?.nivel1?.length ? sud.nivel1 : staticSudFallback.nivel1,
    nivel2: sud?.nivel2?.length ? sud.nivel2 : staticSudFallback.nivel2,
    nivel3: sud?.nivel3?.length ? sud.nivel3 : staticSudFallback.nivel3,
  };
  const naturezaOptions = naturezas?.length ? naturezas : ["PF", "PJ"];

  // State armazena valores MONETÁRIOS como dígitos brutos (ex: "150000" para R$ 1.500,00)
  const [form, setForm] = useState({
    templateId: "",
    group: "ALIMENTACAO",
    subCategory: "",
    natureza: "PF",
    sudNivel1: "",
    sudNivel2: "",
    sudNivel3: "",
    sudVars: "",
    descIn: "",
    descOut: "",
    account: "C6 PJ (Pix)",
    valorPrevisto: "",
    valorReal: "",
    date: todayISO(),
    competenceDate: currMonth(),
    juros: "0",
    desconto: "0",
    acrescimo: "0",
    tarifa: "0",
    modoLancamento: "UNICO",
    parcelaNumero: "",
    parcelaTotal: "",
    recorrencia: "Mensal",
    repeticoes: "12",
    isPaid: false,
    needsSettlement: false,
    statusType: "PENDING",
    settlementDate: "",
    numeroDocumento: "",
    dataEmissao: "",
    paidBy: "",
    reimbursable: false,
    vinculo: "",
  });
  useEffect(() => {
    if (!initialData) return;
    const pd = initialData;
    // Converte valores numéricos do Firestore para dígitos brutos
    const toRawDigits = (val) => {
      if (!val && val !== 0) return "";
      const n = Math.round(Number(val) * 100);
      return String(n);
    };
    setForm({
      // forceNew (duplicar): nunca reaproveita o id — é uma criação nova
      // de verdade, mesmo que o resto dos campos venha copiado.
      id: forceNew ? "" : pd.id || "",
      templateId: pd.templateId || "",
      group: pd.group || pd.type || "ALIMENTACAO",
      subCategory: pd.subCategory || pd.category || pd.group || "",
      natureza: pd.natureza || "PF",
      sudNivel1: pd.sudNivel1 || "",
      sudNivel2: pd.sudNivel2 || "",
      sudNivel3: pd.sudNivel3 || "",
      sudVars: pd.sudVars || "",
      descIn: pd.description || pd.descIn || "",
      descOut: pd.descOut || "",
      account: pd.account || "C6 PJ (Pix)",
      valorPrevisto: toRawDigits(pd.valorPrevisto),
      valorReal: toRawDigits(pd.value),
      date: pd.date || todayISO(),
      competenceDate: pd.competenceDate?.slice(0, 7) || currMonth(),
      juros: toRawDigits(pd.juros) || "0",
      desconto: toRawDigits(pd.desconto) || "0",
      acrescimo: toRawDigits(pd.acrescimo) || "0",
      tarifa: toRawDigits(pd.tarifa) || "0",
      modoLancamento: pd.modoLancamento || "UNICO",
      // Achado real 18/09/2026: o documento só grava o texto combinado
      // "5/10" no campo `parcela` — nunca gravou parcelaNumero/parcelaTotal
      // como números separados. Editar ou duplicar um lançamento parcelado
      // sem isso mostrava os campos "Parcela Nº"/"Total" em branco (mesmo
      // com "Parcelado" selecionado), e salvar sem reparar nisso apagava a
      // parcela de vez, virando "Mensal".
      parcelaNumero: pd.parcelaNumero
        ? String(pd.parcelaNumero)
        : (pd.parcela || "").split("/")[0] || "",
      parcelaTotal: pd.parcelaTotal
        ? String(pd.parcelaTotal)
        : (pd.parcela || "").split("/")[1] || "",
      recorrencia: pd.recorrencia || "Mensal",
      repeticoes: (pd.repetirMeses || pd.repeticoes || "12").toString(),
      isPaid: pd.status === "PAID" || pd.isPaid || false,
      needsSettlement: pd.needsSettlement || false,
      statusType: pd.status || "PENDING",
      settlementDate: pd.settlementDate || "",
      numeroDocumento: pd.numeroDocumento || "",
      dataEmissao: pd.dataEmissao || "",
      paidBy: pd.paidBy || "",
      reimbursable: pd.reimbursable || false,
      vinculo: pd.vinculo || "",
    });
  }, [initialData, forceNew]);

  const isEdit = !!initialData && !forceNew;
  const valorRealNum = rawDigitsToNumber(form.valorReal);

  const groupOptions = useMemo(() => {
    const options = Object.entries(categories || DEFAULT_CATEGORIES).map(
      ([k, v]) => ({
        value: k,
        label: v.label || k,
        nature: v.nature || "EXPENSE",
      }),
    );
    const income = options.filter((o) => o.nature === "INCOME");
    const others = options
      .filter((o) => o.nature !== "INCOME")
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...income, ...others];
  }, [categories]);

  const currentCategory = (categories || DEFAULT_CATEGORIES)[form.group] || {
    subs: [],
  };
  const subs = currentCategory.subs || [];

  const handleTemplateChange = (value) => {
    const tpl = templateOptions.find((t) => t.value === value);
    if (!tpl) return setForm({ ...form, templateId: value });
    const s = tpl.suds || {};
    const isR = tpl.group === "RECEITAS";
    const amt = tpl.amount ? String(Math.round(Number(tpl.amount) * 100)) : "";
    setForm({
      ...form,
      templateId: value,
      group: tpl.group || form.group,
      subCategory: tpl.sub || "",
      sudNivel1: s.nivel1 || "",
      sudNivel2: s.nivel2 || "",
      sudNivel3: s.nivel3 || "",
      sudVars: s.vars || "",
      descIn: tpl.descIn,
      descOut: tpl.descOut,
      account: tpl.account || form.account,
      valorPrevisto: amt,
      natureza: isR ? "PJ" : form.natureza,
      modoLancamento: tpl.parcelado
        ? "PARCELADO"
        : tpl.recorrente
          ? "RECORRENTE"
          : "UNICO",
      parcelaTotal: tpl.parcelaTotal ? String(tpl.parcelaTotal) : "",
    });
  };

  // Grupo: combobox trabalha com o rótulo exibido; resolve pra chave interna
  // (existente) ou cria uma chave nova a partir do texto digitado.
  const groupLabelByKey = Object.fromEntries(
    groupOptions.map((o) => [o.value, o.label]),
  );
  const handleGroupChange = (labelTyped) => {
    const match = groupOptions.find(
      (o) =>
        normalizeTaxonomyValue(o.label) === normalizeTaxonomyValue(labelTyped) ||
        o.value === labelTyped,
    );
    setForm({
      ...form,
      group: match ? match.value : slugifyGroupKey(labelTyped),
      subCategory: "",
    });
  };
  const handleCreateGroup = async (label) => {
    const key = slugifyGroupKey(label);
    if (typeof onAddGroup === "function") await onAddGroup(key, label);
  };
  const handleCreateSubcategory = async (value) => {
    if (typeof onAddSubcategory === "function")
      await onAddSubcategory(form.group, value);
  };
  const handleCreateSudValue = (nivel) => async (value) => {
    if (typeof onAddSudValue === "function") await onAddSudValue(nivel, value);
  };
  const handleCreateNatureza = async (value) => {
    if (typeof onAddNatureza === "function") await onAddNatureza(value);
  };

  const handleSudChange = (field, value) => {
    const updated = { ...form, [field]: value };
    if (field === "sudNivel1") {
      updated.sudNivel2 = "";
      updated.sudNivel3 = "";
    }
    if (field === "sudNivel2") {
      updated.sudNivel3 = "";
    }
    const n1 = updated.sudNivel1 || "",
      n2 = updated.sudNivel2 || "",
      n3 = updated.sudNivel3 || "";
    const vars = updated.sudVars ? ` (${updated.sudVars})` : "";
    // Descrição = reflexo direto do SUD (pedido do titular, 18/09/2026):
    // "Google — Assinaturas Digitais — Rodrigo" (Nível 1 — Nível 2 — Nível 3,
    // pulando níveis vazios) — substitui o texto fixo de "Retirada PJ Recta
    // Scripta" que só fazia sentido pro fluxo específico de retirada PJ.
    if (n1) {
      const partes = [n1, n2, n3].filter(Boolean).join(" — ");
      updated.descIn = partes;
      updated.descOut = `${partes}${vars}`;
    }
    setForm(updated);
  };

  const handleSave = () => {
    const amount = valorRealNum || rawDigitsToNumber(form.valorPrevisto) || 0;
    const isParcelado =
      form.modoLancamento === "PARCELADO" &&
      form.parcelaNumero &&
      form.parcelaTotal;
    // Achado real 18/09/2026: editar "Parcela Nº"/"Total" num lançamento
    // parcelado JÁ EXISTENTE não atualizava o campo interno de parcela —
    // updatePosting() só atualiza este UM documento, sem recalcular nada
    // sozinho (ao contrário da criação nova, onde createPosting() já
    // recalcula por iteração do laço). O número da parcela vai só no campo
    // `parcela` (usado pela etiqueta TIPO) — a descrição fica sempre limpa,
    // sem repetir "(N/Total)" que já aparece na etiqueta.
    const cleanDesc = form.descIn.replace(/\s*\(\d+\/\d+\)\s*$/, "");
    const submitData = {
      ...form,
      description: cleanDesc,
      parcela:
        isEdit && isParcelado
          ? `${form.parcelaNumero}/${form.parcelaTotal}`
          : form.parcela || null,
      amount,
      value: amount,
      juros: rawDigitsToNumber(form.juros),
      desconto: rawDigitsToNumber(form.desconto),
      acrescimo: rawDigitsToNumber(form.acrescimo),
      tarifa: rawDigitsToNumber(form.tarifa),
      valorPrevisto: rawDigitsToNumber(form.valorPrevisto),
      group: form.group,
      type: form.group,
      category: form.subCategory || form.group,
      vinculo: form.vinculo || null,
      natureza: form.natureza || "PF",
      repeatCount:
        form.modoLancamento === "PARCELADO"
          ? parseInt(form.parcelaTotal) || 0
          : parseInt(form.repeticoes) || 0,
      repeatMode:
        form.modoLancamento === "PARCELADO"
          ? "INSTALLMENT"
          : form.modoLancamento === "RECORRENTE"
            ? "RECURRING"
            : "SINGLE",
    };
    onSave(submitData, submitData.repeatMode);
  };

  /** Salva o formulário atual como template no Firestore */
  const handleSaveAsTemplate = () => {
    const amount = valorRealNum || rawDigitsToNumber(form.valorPrevisto) || 0;
    if (!amount || amount <= 0) {
      alert("Defina um valor antes de salvar como template.");
      return;
    }
    if (!form.descIn) {
      alert("Defina uma descrição antes de salvar como template.");
      return;
    }
    const tplName = window.prompt(
      "Nome do template (ex: T-MEU01):",
      `T-${form.group?.slice(0, 3) || "XXX"}01`,
    );
    if (!tplName || !tplName.trim()) return;
    if (typeof onSaveTemplate === "function") {
      onSaveTemplate({
        templateId: tplName.trim(),
        group: form.group,
        sub: form.subCategory,
        descIn: form.descIn,
        descOut: form.descOut,
        account: form.account,
        amount,
        suds: {
          nivel1: form.sudNivel1 || "",
          nivel2: form.sudNivel2 || "",
          nivel3: form.sudNivel3 || "",
          vars: form.sudVars || "",
        },
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0">
          <h3 className="text-sm font-bold text-[#20243B] flex items-center gap-2">
            <i className="fas fa-piggy-bank text-[#C5A059]"></i>
            {isEdit ? "Editar Lançamento" : "Novo Lançamento"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* TEMPLATE */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-copy text-[10px]"></i>Template
            </h4>
            <select
              className={SELECT}
              value={form.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">— Selecione um template —</option>
              {templateOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* CLASSIFICAÇÃO */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-tag text-[10px]"></i>Classificação
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={LABEL}>Grupo</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={groupLabelByKey[form.group] || form.group}
                  options={groupOptions.map((o) => o.label)}
                  placeholder="Grupo"
                  onChange={handleGroupChange}
                  onCreate={handleCreateGroup}
                />
              </div>
              <div>
                <label className={LABEL}>Subcategoria</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={form.subCategory}
                  options={subs}
                  placeholder="—"
                  onChange={(v) => setForm({ ...form, subCategory: v })}
                  onCreate={handleCreateSubcategory}
                />
              </div>
              <div>
                <label className={LABEL}>Natureza</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={form.natureza}
                  options={naturezaOptions}
                  placeholder="PF/PJ"
                  onChange={(v) => setForm({ ...form, natureza: v })}
                  onCreate={handleCreateNatureza}
                />
              </div>
            </div>
          </div>

          {/* SUD */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-sitemap text-[10px]"></i>SUD
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={LABEL}>Nível 1</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={form.sudNivel1}
                  options={sudOptions.nivel1}
                  placeholder="—"
                  onChange={(v) => handleSudChange("sudNivel1", v)}
                  onCreate={handleCreateSudValue(1)}
                />
              </div>
              <div>
                <label className={LABEL}>Nível 2</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={form.sudNivel2}
                  options={sudOptions.nivel2}
                  placeholder="—"
                  onChange={(v) => handleSudChange("sudNivel2", v)}
                  onCreate={handleCreateSudValue(2)}
                />
              </div>
              <div>
                <label className={LABEL}>Nível 3</label>
                <TaxonomyCombobox
                  className={SELECT}
                  value={form.sudNivel3}
                  options={sudOptions.nivel3}
                  placeholder="—"
                  onChange={(v) => handleSudChange("sudNivel3", v)}
                  onCreate={handleCreateSudValue(3)}
                />
              </div>
            </div>
          </div>

          {/* DESCRIÇÃO */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-align-left text-[10px]"></i>Descrição
            </h4>
            <div>
              <label className={LABEL}>Descrição</label>
              <input
                className={INPUT}
                placeholder="Ex: Mercado — Compras do mês"
                value={form.descIn}
                onChange={(e) => setForm({ ...form, descIn: e.target.value })}
              />
            </div>
            <div>
              <label className={LABEL}>Complemento (NF, observações)</label>
              <input
                className={INPUT}
                placeholder="NF, observações..."
                value={form.descOut}
                onChange={(e) => setForm({ ...form, descOut: e.target.value })}
              />
            </div>
          </div>

          {/* VALORES COM MÁSCARA BRASILEIRA */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-dollar-sign text-[10px]"></i>Valores (R$)
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Valor Real (R$)</label>
                <CurrencyInput
                  value={form.valorReal}
                  onChange={onCurrencyChange(setForm, "valorReal")}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className={LABEL}>Valor Previsto (R$)</label>
                <CurrencyInput
                  value={form.valorPrevisto}
                  onChange={onCurrencyChange(setForm, "valorPrevisto")}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className={LABEL}>Juros</label>
                <CurrencyInput
                  value={form.juros}
                  onChange={onCurrencyChange(setForm, "juros")}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className={LABEL}>Desconto</label>
                <CurrencyInput
                  value={form.desconto}
                  onChange={onCurrencyChange(setForm, "desconto")}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className={LABEL}>Acréscimo</label>
                <CurrencyInput
                  value={form.acrescimo}
                  onChange={onCurrencyChange(setForm, "acrescimo")}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className={LABEL}>Tarifa</label>
                <CurrencyInput
                  value={form.tarifa}
                  onChange={onCurrencyChange(setForm, "tarifa")}
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>

          {/* DATAS */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-calendar text-[10px]"></i>Datas
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={LABEL}>Vencimento</label>
                <input
                  type="date"
                  className={INPUT}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL}>Competência</label>
                <input
                  type="month"
                  className={INPUT}
                  value={form.competenceDate}
                  onChange={(e) =>
                    setForm({ ...form, competenceDate: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={LABEL}>Pagamento</label>
                <input
                  type="date"
                  className={INPUT}
                  value={form.settlementDate}
                  onChange={(e) =>
                    setForm({ ...form, settlementDate: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className={LABEL}>Nº Documento</label>
                <input
                  className={INPUT}
                  type="text"
                  placeholder="NF, boleto, etc."
                  value={form.numeroDocumento}
                  onChange={(e) =>
                    setForm({ ...form, numeroDocumento: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={LABEL}>Data de Emissão</label>
                <input
                  type="date"
                  className={INPUT}
                  value={form.dataEmissao}
                  onChange={(e) =>
                    setForm({ ...form, dataEmissao: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* CONTA E RESPONSÁVEL */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-credit-card text-[10px]"></i>Conta e
              Responsável
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Conta</label>
                <select
                  className={SELECT}
                  value={form.account}
                  onChange={(e) =>
                    setForm({ ...form, account: e.target.value })
                  }
                >
                  <option value="">Selecione...</option>
                  {ACCOUNT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Responsável (Vínculo)</label>
                <select
                  className={SELECT}
                  value={form.vinculo}
                  onChange={(e) =>
                    setForm({ ...form, vinculo: e.target.value })
                  }
                >
                  <option value="">—</option>
                  <option value="eliane">ELIANE</option>
                  <option value="rodrigo">RODRIGO</option>
                  <option value="cauê">CAUÊ</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded text-[#20243B]"
                  checked={form.reimbursable}
                  onChange={(e) =>
                    setForm({ ...form, reimbursable: e.target.checked })
                  }
                />
                <span className="text-[10px] text-gray-600">Reembolsável</span>
              </label>
            </div>
          </div>

          {/* RECORRÊNCIA */}
          <div className={SECTION}>
            <h4 className={SEC_TITLE}>
              <i className="fas fa-redo text-[10px]"></i>Recorrência
            </h4>
            <div className="flex gap-2 mb-3">
              {[
                {
                  id: "UNICO",
                  label: "Único",
                  color: "border-gray-300 text-gray-600",
                },
                {
                  id: "PARCELADO",
                  label: "Parcelado",
                  color: "border-blue-300 text-blue-700",
                },
                {
                  id: "RECORRENTE",
                  label: "Recorrente",
                  color: "border-orange-300 text-orange-700",
                },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setForm({ ...form, modoLancamento: m.id })}
                  className={`flex-1 py-1.5 px-2 rounded text-[10px] font-bold border transition ${form.modoLancamento === m.id ? `bg-white ${m.color} shadow-sm` : "border-transparent text-gray-500 hover:bg-gray-100"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.modoLancamento === "PARCELADO" && (
                <>
                  <div>
                    <label className={LABEL}>Parcela Nº</label>
                    <input
                      type="number"
                      min="1"
                      className={INPUT}
                      value={form.parcelaNumero}
                      onChange={(e) =>
                        setForm({ ...form, parcelaNumero: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Total de Parcelas</label>
                    <input
                      type="number"
                      min="2"
                      max="60"
                      className={INPUT}
                      value={form.parcelaTotal}
                      onChange={(e) =>
                        setForm({ ...form, parcelaTotal: e.target.value })
                      }
                    />
                  </div>
                </>
              )}
              {form.modoLancamento === "RECORRENTE" && (
                <>
                  <div>
                    <label className={LABEL}>Recorrência</label>
                    <select
                      className={SELECT}
                      value={form.recorrencia}
                      onChange={(e) =>
                        setForm({ ...form, recorrencia: e.target.value })
                      }
                    >
                      <option value="Mensal">Mensal</option>
                      <option value="Bimestral">Bimestral</option>
                      <option value="Trimestral">Trimestral</option>
                      <option value="Semestral">Semestral</option>
                      <option value="Anual">Anual</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Repetições</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      className={INPUT}
                      value={form.repeticoes}
                      onChange={(e) =>
                        setForm({ ...form, repeticoes: e.target.value })
                      }
                    />
                  </div>
                </>
              )}
            </div>
            <div className="mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded text-[#20243B]"
                  checked={form.isPaid}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      isPaid: e.target.checked,
                      statusType: e.target.checked ? "PAID" : "PENDING",
                    })
                  }
                />
                <span className="text-[10px] text-gray-600">
                  {form.isPaid ? "✅ Pago" : "Pendente"}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[10px] font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveAsTemplate}
            disabled={
              !form.descIn ||
              !(valorRealNum || rawDigitsToNumber(form.valorPrevisto))
            }
            className="px-4 py-2 text-[10px] font-bold text-[#C5A059] bg-white border border-[#C5A059] rounded-lg hover:bg-[#C5A059]/10 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Salvar dados atuais como template reutilizável"
          >
            <i className="fas fa-save text-[9px] mr-1"></i>Salvar Template
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-[10px] font-bold text-white bg-[#20243B] rounded-lg hover:bg-[#1a1e30]"
          >
            {isEdit ? "Atualizar" : "Criar Lançamento"}
          </button>
        </div>
      </div>
    </div>
  );
}
